/**
 * Guest sessions use Better Auth's HTTP endpoints directly: a full auth client is not
 * needed for "sign in anonymously", and keeping it out keeps the Go Live page small.
 * (The verification UI can lazy-load better-auth/client when it is built.)
 */
export async function ensureSession(): Promise<{ userId: string; isAnonymous: boolean }> {
  const existing = await fetch('/api/auth/get-session', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null));
  const user = (existing as { user?: { id: string; isAnonymous?: boolean } } | null)?.user;
  if (user) return { userId: user.id, isAnonymous: Boolean(user.isAnonymous) };
  const res = await fetch('/api/auth/sign-in/anonymous', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) throw new Error('Could not start a session. Please try again.');
  const body = (await res.json()) as { user: { id: string } };
  return { userId: body.user.id, isAnonymous: true };
}

async function authPost(path: string, body: unknown) {
  const res = await fetch(`/api/auth${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data as { message?: string } | null)?.message ?? 'Something went wrong. Please try again.');
  return data;
}

/** Email verification. Signing in from a guest session moves the guest's streams to the verified account. */
export const emailCode = {
  send: (email: string) => authPost('/email-otp/send-verification-otp', { email, type: 'sign-in' }),
  verify: (email: string, otp: string) => authPost('/sign-in/email-otp', { email, otp }),
};

/** Phone verification (SMS via Prelude in staging/production). Same guest → account upgrade. */
export const phoneCode = {
  send: (phoneNumber: string) => authPost('/phone-number/send-otp', { phoneNumber }),
  verify: (phoneNumber: string, code: string) => authPost('/phone-number/verify', { phoneNumber, code }),
};
